import type {
  DashboardRange,
  PortfolioAttachFailure,
  PortfolioEligibleCampaign,
} from "@adpilot/shared";
import {
  IconChevronLeft,
  IconChevronRight,
  IconInfoCircle,
  IconZoomExclamation,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CampaignTypeBadge } from "@/components/campaign/CampaignTypeBadge";
import { SearchInput } from "@/components/patterns/SearchInput";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { getApiErrorMessage } from "@/lib/api-error";
import { useFormatters } from "@/lib/format";
import { attachPortfolioCampaigns, fetchEligibleCampaigns } from "@/lib/portfolio-api";
import { cn } from "@/lib/utils";

type CampaignTypeFilter = PortfolioEligibleCampaign["campaignType"];
type StatusFilter = PortfolioEligibleCampaign["status"] | null;
type SortKey = "dailyBudget" | "spend" | "acos";

const PAGE_SIZES = [10, 25, 50] as const;
/** Mirrors portfolioAttachCampaignsInputSchema's max(50) per request. */
const ATTACH_BATCH_LIMIT = 50;

interface AddCampaignsDialogProps {
  portfolioId: string;
  range: DashboardRange;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Xnurta parity (ADP-137):「添加广告活动」dual-panel transfer dialog. The left
 * panel filters/pages the eligible list client-side (the API returns the full
 * shop-scoped set); the right panel stages the selection. Only 确定 writes.
 */
export function AddCampaignsDialog({
  portfolioId,
  range,
  open,
  onOpenChange,
}: AddCampaignsDialogProps) {
  const { t } = useTranslation("portfolios");
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<CampaignTypeFilter>("sp");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [staged, setStaged] = useState<Map<string, PortfolioEligibleCampaign>>(new Map());

  const query = useQuery({
    queryKey: ["portfolios", "eligible-campaigns", portfolioId, range],
    queryFn: () => fetchEligibleCampaigns(portfolioId, range),
    enabled: open,
  });

  const mutation = useMutation({
    // The API caps one request at ATTACH_BATCH_LIMIT ids;「全部添加」can stage
    // far more, so submit sequential batches and merge the per-campaign outcomes.
    mutationFn: async (campaignIds: string[]) => {
      const attached: string[] = [];
      const failed: PortfolioAttachFailure[] = [];
      for (let offset = 0; offset < campaignIds.length; offset += ATTACH_BATCH_LIMIT) {
        const batch = campaignIds.slice(offset, offset + ATTACH_BATCH_LIMIT);
        const result = await attachPortfolioCampaigns(portfolioId, batch);
        attached.push(...result.attached);
        failed.push(...result.failed);
      }
      return { attached, failed };
    },
    onSuccess: async (result) => {
      if (result.attached.length > 0) {
        await queryClient.invalidateQueries({ queryKey: ["portfolios"] });
      }
      if (result.failed.length === 0) {
        toast.success(t("addCampaigns.success", { count: result.attached.length }));
        closeDialog(false);
        return;
      }
      if (result.attached.length > 0) {
        toast.warning(
          t("addCampaigns.partial", {
            succeeded: result.attached.length,
            failed: result.failed.length,
          }),
        );
        closeDialog(false);
        return;
      }
      // Everything failed — keep the dialog open with the selection intact.
      toast.error(t("addCampaigns.failed", { message: result.failed[0]?.message ?? "" }).trim());
    },
    onError: async (error) => {
      // An earlier batch may already have written before a later one threw.
      await queryClient.invalidateQueries({ queryKey: ["portfolios"] });
      toast.error(await getApiErrorMessage(error));
    },
  });

  function closeDialog(nextOpen: boolean) {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setSearch("");
      setTypeFilter("sp");
      setStatusFilter(null);
      setSort(null);
      setPage(1);
      setStaged(new Map());
    }
  }

  const eligible = query.data ?? [];
  // `matching` applies the filters only; the source table additionally hides
  // staged rows. Keeping both lets the empty state distinguish "everything the
  // filter matches is already staged" from "the filter matches nothing".
  const matching = useMemo(() => {
    const term = search.trim().toLowerCase();
    return eligible.filter(
      (campaign) =>
        campaign.campaignType === typeFilter &&
        (statusFilter == null || campaign.status === statusFilter) &&
        (term === "" || campaign.name.toLowerCase().includes(term)),
    );
  }, [eligible, typeFilter, statusFilter, search]);
  const filtered = useMemo(() => {
    const rows = matching.filter((campaign) => !staged.has(campaign.id));
    if (!sort) return rows;
    const value = (campaign: PortfolioEligibleCampaign) =>
      sort.key === "dailyBudget"
        ? (campaign.dailyBudget ?? -1)
        : sort.key === "spend"
          ? campaign.metrics.spend
          : campaign.metrics.acos;
    return [...rows].sort((a, b) =>
      sort.dir === "asc" ? value(a) - value(b) : value(b) - value(a),
    );
  }, [matching, staged, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function stage(campaigns: PortfolioEligibleCampaign[]) {
    setStaged((previous) => {
      const next = new Map(previous);
      for (const campaign of campaigns) next.set(campaign.id, campaign);
      return next;
    });
  }

  function unstage(ids: string[]) {
    setStaged((previous) => {
      const next = new Map(previous);
      for (const id of ids) next.delete(id);
      return next;
    });
  }

  function toggleSort(key: SortKey) {
    setSort((previous) =>
      previous?.key === key
        ? previous.dir === "desc"
          ? { key, dir: "asc" }
          : null
        : { key, dir: "desc" },
    );
  }

  const stagedRows = [...staged.values()];
  // "All added" only when the current filter DOES match campaigns and every one
  // of them is staged — a filter that matches nothing shows no-results instead.
  const allAdded = matching.length > 0 && filtered.length === 0;

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogPopup className="max-w-[960px]">
        <DialogHeader>
          <DialogTitle>{t("addCampaigns.title")}</DialogTitle>
        </DialogHeader>
        <DialogPanel className="flex flex-col gap-3">
          <p className="flex items-start gap-1.5 text-[11px] leading-4 text-muted-foreground">
            <IconInfoCircle
              className="mt-px size-3.5 shrink-0 text-warning-foreground"
              aria-hidden="true"
            />
            {t("addCampaigns.banner")}
          </p>
          <div className="grid min-h-[360px] grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-3">
            {/* Left: eligible source list */}
            <div className="flex min-w-0 flex-col rounded-[10px] border border-line">
              <div className="flex flex-col gap-2 p-2.5">
                <SearchInput
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder={t("addCampaigns.searchPlaceholder")}
                  showIcon
                  className="w-full"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    items={[
                      { value: "sp", label: "SP" },
                      { value: "sb", label: "SB" },
                      { value: "sd", label: "SD" },
                    ]}
                    value={typeFilter}
                    onValueChange={(value) => {
                      setTypeFilter(value as CampaignTypeFilter);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger
                      className="h-8 w-full"
                      aria-label={t("addCampaigns.typePlaceholder")}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="sp">SP</SelectItem>
                        <SelectItem value="sb">SB</SelectItem>
                        <SelectItem value="sd">SD</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Select
                    items={[
                      { value: null, label: t("addCampaigns.statusPlaceholder") },
                      { value: "enabled", label: t("state.enabled") },
                      { value: "paused", label: t("state.paused") },
                      { value: "archived", label: t("state.archived") },
                    ]}
                    value={statusFilter}
                    onValueChange={(value) => {
                      setStatusFilter(value as StatusFilter);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger
                      className="h-8 w-full"
                      aria-label={t("addCampaigns.statusPlaceholder")}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value={null}>{t("addCampaigns.statusPlaceholder")}</SelectItem>
                        <SelectItem value="enabled">{t("state.enabled")}</SelectItem>
                        <SelectItem value="paused">{t("state.paused")}</SelectItem>
                        <SelectItem value="archived">{t("state.archived")}</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-auto paper-scrollbar border-t border-line">
                {query.isLoading ? (
                  <div className="flex h-full min-h-[220px] items-center justify-center">
                    <Spinner />
                  </div>
                ) : query.isError ? (
                  <ErrorState message={t("loadFailed")} onRetry={() => void query.refetch()} />
                ) : (
                  <SourceTable
                    rows={pageRows}
                    filteredCount={filtered.length}
                    eligibleCount={eligible.length}
                    allAdded={allAdded}
                    sort={sort}
                    onToggleSort={toggleSort}
                    onAdd={(campaign) => stage([campaign])}
                    onAddAll={() => stage(filtered)}
                  />
                )}
              </div>
              <div className="flex items-center justify-between gap-2 border-t border-line px-2.5 py-2 text-[10px] text-muted-foreground">
                <span>{t("addCampaigns.totalCount", { count: filtered.length })}</span>
                <div className="flex items-center gap-1.5">
                  <Select
                    items={PAGE_SIZES.map((size) => ({
                      value: size,
                      label: t("addCampaigns.pageSize", { size }),
                    }))}
                    value={pageSize}
                    onValueChange={(value) => {
                      setPageSize(value as number);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger
                      className="h-6 min-w-[76px] text-[10px]"
                      aria-label={t("addCampaigns.pageSize", { size: pageSize })}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {PAGE_SIZES.map((size) => (
                          <SelectItem key={size} value={size}>
                            {t("addCampaigns.pageSize", { size })}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon-xs"
                    aria-label={t("addCampaigns.previousPage")}
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    <IconChevronLeft />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon-xs"
                    aria-label={t("addCampaigns.nextPage")}
                    disabled={currentPage >= pageCount}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    <IconChevronRight />
                  </Button>
                  <span className="flex items-center gap-1">
                    {t("addCampaigns.goToPagePrefix")}
                    <Input
                      type="number"
                      min={1}
                      max={pageCount}
                      value={currentPage}
                      onChange={(event) => {
                        const next = Number(event.target.value);
                        if (Number.isInteger(next)) {
                          setPage(Math.min(Math.max(next, 1), pageCount));
                        }
                      }}
                      aria-label={t("addCampaigns.goToPageAria")}
                      className="h-6 w-12 px-1 text-center text-[10px]"
                    />
                    {t("addCampaigns.goToPageSuffix")}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: staged selection */}
            <div className="flex min-w-0 flex-col rounded-[10px] border border-line">
              <div className="flex h-9 shrink-0 items-center justify-between border-b border-line px-2.5">
                <span className="text-[12px] font-medium text-ink">
                  {t("addCampaigns.addedCount", { count: staged.size })}
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  className="text-muted-foreground"
                  disabled={staged.size === 0}
                  onClick={() => setStaged(new Map())}
                >
                  {t("addCampaigns.removeAll")}
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-auto paper-scrollbar">
                {stagedRows.length === 0 ? (
                  <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-2 text-muted-foreground/60">
                    <IconZoomExclamation className="size-10" aria-hidden="true" />
                    <span className="text-[11px]">{t("addCampaigns.emptyRight")}</span>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-[minmax(0,1fr)_64px_44px_56px] items-center gap-x-2 border-b border-line bg-subtle px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground">
                      <span>{t("addCampaigns.columns.name")}</span>
                      <span>{t("addCampaigns.columns.status")}</span>
                      <span>{t("addCampaigns.columns.type")}</span>
                      <span>{t("addCampaigns.columns.action")}</span>
                    </div>
                    {stagedRows.map((campaign) => (
                      <div
                        key={campaign.id}
                        className="grid grid-cols-[minmax(0,1fr)_64px_44px_56px] items-center gap-x-2 border-b border-line/60 px-2.5 py-1.5 text-[11px]"
                      >
                        <span className="truncate text-ink" title={campaign.name}>
                          {campaign.name}
                        </span>
                        <StatusCell status={campaign.status} />
                        <span>
                          <CampaignTypeBadge campaignType={campaign.campaignType} />
                        </span>
                        <Button
                          variant="outline"
                          size="xs"
                          className="justify-self-start"
                          onClick={() => unstage([campaign.id])}
                        >
                          {t("addCampaigns.remove")}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => closeDialog(false)}
          >
            {t("addCampaigns.cancel")}
          </Button>
          <Button
            disabled={staged.size === 0 || mutation.isPending}
            onClick={() => mutation.mutate([...staged.keys()])}
          >
            {mutation.isPending ? <Spinner data-icon="inline-start" /> : null}
            {t("addCampaigns.confirm")}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

interface SourceTableProps {
  rows: PortfolioEligibleCampaign[];
  filteredCount: number;
  eligibleCount: number;
  allAdded: boolean;
  sort: { key: SortKey; dir: "asc" | "desc" } | null;
  onToggleSort: (key: SortKey) => void;
  onAdd: (campaign: PortfolioEligibleCampaign) => void;
  onAddAll: () => void;
}

const SOURCE_GRID = "grid grid-cols-[minmax(0,1.5fr)_60px_40px_64px_64px_52px_64px] gap-x-2";

function SourceTable({
  rows,
  filteredCount,
  eligibleCount,
  allAdded,
  sort,
  onToggleSort,
  onAdd,
  onAddAll,
}: SourceTableProps) {
  const { t } = useTranslation("portfolios");
  const { formatCurrency, formatPercent, formatDate } = useFormatters();

  if (filteredCount === 0) {
    return (
      <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-2 text-muted-foreground/60">
        <IconZoomExclamation className="size-10" aria-hidden="true" />
        <span className="text-[11px]">
          {eligibleCount === 0
            ? t("addCampaigns.empty.title")
            : allAdded
              ? t("addCampaigns.allAddedEmpty")
              : t("addCampaigns.noSearchResults")}
        </span>
        {eligibleCount === 0 ? (
          <span className="max-w-[260px] text-center text-[10px]">
            {t("addCampaigns.empty.description")}
          </span>
        ) : null}
      </div>
    );
  }

  const sortHeader = (key: SortKey, label: string) => (
    <button
      type="button"
      onClick={() => onToggleSort(key)}
      className={cn(
        "flex items-center justify-end gap-0.5 text-right hover:text-ink",
        sort?.key === key && "text-ink",
      )}
      aria-label={t("addCampaigns.sortBy", { column: label })}
    >
      {label}
      <span aria-hidden="true" className="text-[8px] leading-none">
        {sort?.key === key ? (sort.dir === "desc" ? "▼" : "▲") : "⇅"}
      </span>
    </button>
  );

  return (
    <div className="min-w-[480px]">
      <div
        className={cn(
          SOURCE_GRID,
          "sticky top-0 z-10 items-center border-b border-line bg-subtle px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground",
        )}
      >
        <span>{t("addCampaigns.columns.name")}</span>
        <span>{t("addCampaigns.columns.status")}</span>
        <span>{t("addCampaigns.columns.type")}</span>
        {sortHeader("dailyBudget", t("addCampaigns.columns.budget"))}
        {sortHeader("spend", t("addCampaigns.columns.spend"))}
        {sortHeader("acos", t("addCampaigns.columns.acos"))}
        <Button
          variant="ghost"
          size="xs"
          className="justify-start px-0 text-primary"
          disabled={rows.length === 0}
          onClick={onAddAll}
        >
          {t("addCampaigns.addAll")}
        </Button>
      </div>
      {rows.map((campaign) => (
        <div
          key={campaign.id}
          className={cn(
            SOURCE_GRID,
            "group items-center border-b border-line/60 px-2.5 py-1.5 text-[11px] hover:bg-subtle",
          )}
        >
          <span className="min-w-0">
            <span className="block truncate text-ink" title={campaign.name}>
              {campaign.name}
            </span>
            <span className="block truncate text-[9px] text-muted-foreground">
              {campaign.startDate == null ? "—" : formatDate(campaign.startDate)}
              {" ~ "}
              {campaign.endDate == null
                ? t("addCampaigns.noEndDate")
                : formatDate(campaign.endDate)}
            </span>
          </span>
          <StatusCell status={campaign.status} />
          <span>
            <CampaignTypeBadge campaignType={campaign.campaignType} />
          </span>
          <span className="text-right font-mono tabular-nums text-ink">
            {campaign.dailyBudget == null ? "—" : formatCurrency(campaign.dailyBudget)}
          </span>
          <span className="text-right font-mono tabular-nums text-ink">
            {formatCurrency(campaign.metrics.spend)}
          </span>
          <span className="text-right font-mono tabular-nums text-muted-foreground">
            {campaign.metrics.acos > 0 ? formatPercent(campaign.metrics.acos) : "—"}
          </span>
          <Button
            variant="outline"
            size="xs"
            className="justify-self-start"
            onClick={() => onAdd(campaign)}
          >
            {t("addCampaigns.addRow")}
          </Button>
        </div>
      ))}
    </div>
  );
}

function StatusCell({ status }: { status: PortfolioEligibleCampaign["status"] }) {
  const { t } = useTranslation("portfolios");
  return (
    <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
      <span
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          status === "enabled"
            ? "bg-success"
            : status === "paused"
              ? "bg-warning"
              : "bg-muted-foreground/50",
        )}
        aria-hidden="true"
      />
      {t(`state.${status}`)}
    </span>
  );
}
